import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { IonSearchbar, IonSegment, SegmentCustomEvent } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  PrimarySubscriptionAccessActionEnum,
  PrimarySubscriptionPlanAccessAliasEnum
} from '../../../../enums/monetization';
import { WalkingFarmMainTabEnum } from '../../../../enums/walking-farm';
import * as fromWalkingFarmActions from '../../../../store/actions/walking-farm.actions';
import * as fromWalkingFarmSelectors from '../../../../store/selectors/walking-farm.selectors';
import { MapsService } from '../../../services/maps.service';
import { MonetizationService } from '../../../services/monetization.service';

@Component({
  animations: [
    trigger('toolbarSwitchDrawMode', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('110ms ease-out', style({ height: 44, opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: 44, opacity: 1 }),
        animate('110ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-home',
  standalone: false,
  templateUrl: './home.page.html'
})
export class HomePageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private mapsService = inject(MapsService);
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  @ViewChild(IonSearchbar, { read: IonSearchbar, static: false })
  searchBarInput?: IonSearchbar;

  @ViewChild(IonSegment, { static: false })
  ionSegment?: IonSegment;

  selectedTab$: Observable<WalkingFarmMainTabEnum>;
  mapInstance: google.maps.Map | null = null;
  selectedMapTypeId: google.maps.MapTypeId | null = null;

  savedFarmsTextFiler$: Observable<string>;
  drawMode$: Observable<boolean>;
  filtersAppliedNumber$: Observable<number>;

  defaultBackHref: string | undefined;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];

    this.filtersAppliedNumber$ = this.store.pipe(select(fromWalkingFarmSelectors.selectFiltersAppliedNumber));
    this.savedFarmsTextFiler$ = this.store.pipe(select(fromWalkingFarmSelectors.selectSavedFarmsTextFilter));
    this.drawMode$ = this.store.pipe(select(fromWalkingFarmSelectors.selectDrawMode));
    this.selectedTab$ = this.store.pipe(select(fromWalkingFarmSelectors.selectMainSelectedTab));
    this.store
      .pipe(select(fromWalkingFarmSelectors.selectMainSelectedTab), distinctUntilChanged())
      .subscribe(async (selectedTab) => {
        if (selectedTab === WalkingFarmMainTabEnum.Saved) {
          await this.refreshSavedFarms();
        }
      });
  }

  async ionViewWillEnter() {
    await this.mapsService.enableWatchPosition();
  }

  async ionViewWillLeave() {
    await this.mapsService.disableWatchPosition();
  }

  async segmentChange(event: SegmentCustomEvent) {
    let hasAccess = true;
    if (event.detail.value === WalkingFarmMainTabEnum.Saved) {
      hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.B]);
      if (!hasAccess && this.ionSegment) {
        setTimeout(() => {
          if (this.ionSegment) {
            this.ionSegment.value = WalkingFarmMainTabEnum.New;
          }
        });
      }
    }
    if (hasAccess) {
      this.store.dispatch(
        fromWalkingFarmActions.setMainSelectedTab({
          payload: { selectedTab: event.detail.value as WalkingFarmMainTabEnum }
        })
      );
    }
  }

  filterSavedFarms(event: CustomEvent) {
    this.store.dispatch(
      fromWalkingFarmActions.updateSavedFarmsListFilter({
        payload: {
          text: event.detail.value
        }
      })
    );
  }

  async refreshSavedFarms() {
    this.store.dispatch(fromWalkingFarmActions.loadSavedFarmsList());
    await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.C], {
      accessAction: PrimarySubscriptionAccessActionEnum.ViewSavedFarmsList
    });
  }

  readonly walkingFarmMainTabs = WalkingFarmMainTabEnum;
}
